import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef } from 'react';
import { IBuildingProviderContext } from '../@types/ContextProviderRelated';
import CameraPositionSpriteModel from '../models/standard/CameraPositionSpriteModel';
import { useAnnotations } from '../provider/AnnotationsProvider';
import { useHistory } from '../provider/HistoryProvider';

const MAX_HISTORY_COUNT = 20;

// Debounced actions commit only after user pauses for this long.
const BUFFER_DELAY = 500; // ms  (raise to 800-1200ms if you want fewer history entries while dragging)

// If SAME action is committed twice and the resulting building is identical,
// we treat it as a duplicate and do not push a new entry.
const DOUBLE_UPDATE_THRESHOLD = 2000; // ms (this is just a guard; actual dedupe uses isEqual)

// If commit happens almost instantly after last one, treat as "phantom" auto-update and overwrite last.
const RAPID_UPDATE_THRESHOLD = 50; // ms

export interface IHistoryEntry {
  building: IBuildingProviderContext;
  cameraPositionSprites?: CameraPositionSpriteModel[];
  roomCompleted: boolean;
  timestamp: number;
  action: EHistoryAction;
}

export enum EHistoryAction {
  UNKNOWN = 'History.unknown',
  UNDONE = 'History.undone',
  REDONE = 'History.redone',
  ATTRIBUTE_CHANGED = 'History.attributeChanged',
  BUILDING_CREATED = 'History.buildingCreated',
  RESET_TO_DEFAULT = 'History.resetToDefault',
  WALLS_CHANGED = 'History.wallsChanged',
  FLOOR_COLOR_CHANGED = 'History.floorColorChanged',
  ROOM_TITLE_CHANGED = 'History.roomTitleChanged',
  ROOM_DELETED = 'History.roomDeleted',
  ROOM_ATTRIBUES_UPDATED = 'History.roomAttributesUpdated',
  ROOM_GEOMETRY_ATTRIBUTES_UPDATED = 'History.geometryAttributesUpdated',
  FLOOR_TILES_CREATED = 'History.floorTilesCreated',
  FLOOR_TILES_SET = 'History.floorTilesSet',
  FLOOR_TILES_UPDATED = 'History.floorTilesUpdated',
  FLOOR_TILES_CHANGED = 'History.floorTilesChanged',
  FLOOR_TILES_DELETED = 'History.floorTilesDeleted',
  FLOOR_TEXTURE_CHANGED = 'History.floorTextureChanged',
  FLOOR_ATTRIBUTES_UPDATED = 'History.floorAttributesUpdated',
  FLOOR_GEOMETRY_ATTRIBUTES_UPDATED = 'Histor.floorGeometryAttributesUpdated',
  FLOOR_RESET = 'History.floorReset',
  ALL_WALL_LENGTHS_UPDATED = 'History.allWallLengthUpdated',
  WALL_THICKNESS_CHANGED = 'History.wallThicknessChanged',
  WALL_SPLITTED = 'History.wallSplitted',
  WALL_LENGTH_CHANGED = 'History.wallLengthChanged',
  WALL_PARTITION_ADDED = 'History.wallPartitionAdded',
  WALL_PARTITION_LENGTH_CHANGED = 'History.wallPartitionLengthChanged',
  WALL_PARTITION_THICKNESS_CHANGED = 'History.wallPartitionThicknessChanged',
  WALL_PARTITION_DELETED = 'History.wallPartitionDeleted',
  WALL_STANDARD_ATTRIBUTE_UPDATED = 'History.standardWallAttributeUpdated',
  WALL_STANDARD_GEOMETRY_ATTRIBUTE_UPDATED = 'History.standardWallGeometryAttributeUpdated',
  WALL_PARTITION_ATTRIBUTE_UPDATED = 'History.partitionWallAttributeUpdated',
  WALL_PARTITION_GEOMETRY_ATTRIBUTE_UPDATED = 'History.partitionWallGeometryAttributeUpdated',
  NODE_ADDED = 'History.nodeAdded',
  WALL_DELETED = 'History.wallDeleted',
  WINDOW_ADDED = 'History.windowAdded',
  WINDOW_POSITION_CHANGED = 'History.windowPositionChanged',
  WINDOW_DELETED = 'History.windowDeleted',
  WINDOW_WIDTH_CHANGED = 'History.windowWidthChanged',
  DOOR_ADDED = 'History.doorAdded',
  DOOR_POSITION_CHANGED = 'History.doorPositionChanged',
  DOOR_DELETED = 'History.doorDeleted',
  DOOR_WIDTH_CHANGED = 'History.doorWidthChanged',
  DOOR_OPENING_DIRECTION_CHANGED = 'History.doorDirectionChanged',
  DOOR_ORIENTATION_CHANGED = 'History.doorOrientationChanged',
  DOOR_ATTRIBUTES_UPDATED = 'History.doorAttributesUpdated',
  DOOR_WIDTH_AND_DIRECTION_ATTRIBUTES_UPDATED = 'History.doorWidhtAndDirectionAttributesUpdated',
  PASSAGE_ADDED = 'History.passageAdded',
  PASSAGE_POSITION_CHANGED = 'History.passagePositionChanged',
  PASSAGE_DELETED = 'History.passageDeleted',
  PASSAGE_WIDTH_CHANGED = 'History.passageWidthChanged',
  PASSAGE_ATTRIBUTES_UPDATED = 'History.passageAttributesUpdated',
  PASSAGE_WIDTH_ATTRIBUTES_UPDATED = 'History.passageWidthAttributesUpdated',
  ELEMENT_ADDED = 'History.elementAdded',
  ELEMENT_SET = 'History.elementSet',
  ELEMENT_DELETED = 'History.elementDeleted',
  ELEMENT_UPDATED = 'History.elementUpdated',
  ELEMENT_ROTATION_CHANGED = 'History.elementRotationChanged',
  ELEMENT_TEXTURE_CHANGED = 'History.elementTextureChanged',
  MODEL2D_ADDED = 'History.model2dAdded',
  MODEL2D_SET = 'History.model2dSet',
  MODEL2D_DELETED = 'History.model2dDeleted',
  MODEL2D_UPDATED = 'History.model2dUpdated',
  MODEL2D_ROTATION_CHANGED = 'History.model2dRotationChanged',
  MODEL2D_TEXTURE_CHANGED = 'History.model2dTextureChanged',
  PRODUCT_ADDED = 'History.productAdded',
  PRODUCT_SET = 'History.productSet',
  PRODUCT_DELETED = 'History.productDeleted',
  PRODUCT_UPDATED = 'History.productUpdated',
  PRODUCT_ROTATION_CHANGED = 'History.productRotationChanged',
  RADIATOR_ADDED = 'History.radiatorAdded',
  RADIATOR_POSITION_CHANGED = 'History.radiatorPositionChanged',
  RADIATOR_DELETED = 'History.radiatorDeleted',
  RADIATOR_WIDTH_CHANGED = 'History.radiatorWidthChanged',
  RADIATOR_THICKNESS_CHANGED = 'History.radiatorThicknessChanged',
  RADIATOR_ATTRIBUTES_UPDATED = 'History.radiatorAttributesUpdated',
  RADIATOR_DEPTH_ATTRIBUTES_UPDATED = 'History.radiatorDepthAttributesUpdated',
  RADIATOR_LENGHT_ATTRIBUTES_UPDATED = 'History.radiatorLenghtAttributesUpdated',
  ROOM_CREATED = 'History.roomCreated',
}

//Action sets that should be debounced.
export const debounceHistoryActions = new Set([
  EHistoryAction.WALLS_CHANGED,
  EHistoryAction.WALL_THICKNESS_CHANGED,
  EHistoryAction.WALL_LENGTH_CHANGED,
  EHistoryAction.WALL_PARTITION_LENGTH_CHANGED,
  EHistoryAction.WALL_PARTITION_THICKNESS_CHANGED,
  EHistoryAction.WINDOW_WIDTH_CHANGED,
  EHistoryAction.WINDOW_POSITION_CHANGED,
  EHistoryAction.DOOR_WIDTH_CHANGED,
  EHistoryAction.DOOR_POSITION_CHANGED,
  EHistoryAction.PASSAGE_WIDTH_CHANGED,
  EHistoryAction.PASSAGE_POSITION_CHANGED,
  EHistoryAction.ROOM_TITLE_CHANGED,
  EHistoryAction.ELEMENT_ROTATION_CHANGED,
  EHistoryAction.ELEMENT_UPDATED,
  EHistoryAction.MODEL2D_ROTATION_CHANGED,
  EHistoryAction.MODEL2D_UPDATED,
  EHistoryAction.PRODUCT_ROTATION_CHANGED,
  EHistoryAction.PRODUCT_UPDATED,
  EHistoryAction.RADIATOR_POSITION_CHANGED,
  EHistoryAction.RADIATOR_WIDTH_CHANGED,
  EHistoryAction.RADIATOR_THICKNESS_CHANGED,
]);

export const actionsToIgnore = new Set([
  EHistoryAction.UNKNOWN,
  EHistoryAction.ROOM_ATTRIBUES_UPDATED,
  EHistoryAction.ROOM_GEOMETRY_ATTRIBUTES_UPDATED,
  EHistoryAction.FLOOR_ATTRIBUTES_UPDATED,
  EHistoryAction.FLOOR_GEOMETRY_ATTRIBUTES_UPDATED,
  EHistoryAction.FLOOR_ATTRIBUTES_UPDATED,
  EHistoryAction.FLOOR_GEOMETRY_ATTRIBUTES_UPDATED,
  EHistoryAction.WALL_STANDARD_ATTRIBUTE_UPDATED,
  EHistoryAction.WALL_STANDARD_GEOMETRY_ATTRIBUTE_UPDATED,
  EHistoryAction.WALL_PARTITION_ATTRIBUTE_UPDATED,
  EHistoryAction.WALL_PARTITION_GEOMETRY_ATTRIBUTE_UPDATED,
  EHistoryAction.DOOR_ATTRIBUTES_UPDATED,
  EHistoryAction.DOOR_WIDTH_AND_DIRECTION_ATTRIBUTES_UPDATED,
  EHistoryAction.PASSAGE_ATTRIBUTES_UPDATED,
  EHistoryAction.PASSAGE_WIDTH_ATTRIBUTES_UPDATED,
  EHistoryAction.RADIATOR_ATTRIBUTES_UPDATED,
  EHistoryAction.RADIATOR_DEPTH_ATTRIBUTES_UPDATED,
  EHistoryAction.RADIATOR_LENGHT_ATTRIBUTES_UPDATED,
]);

export const historyEntryDefaultValue: IHistoryEntry | undefined = undefined;

type WallAttributeCarrier = {
  uuid?: string;
  attributes?: unknown[];
};

type BoundaryWithWallRefs = {
  walls?: WallAttributeCarrier[];
  edges?: Array<{ wall?: WallAttributeCarrier }>;
};

type SpaceWithWallRefs = {
  walls?: WallAttributeCarrier[];
  boundaries?: BoundaryWithWallRefs[];
};

type RoomWithWallRefs = {
  spaces?: SpaceWithWallRefs[];
};

const collectWallAttributesByUuid = (building: IBuildingProviderContext): Map<string, unknown[]> => {
  const attributesByUuid = new Map<string, unknown[]>();

  const rememberWallAttributes = (wall?: WallAttributeCarrier | null) => {
    if (!wall?.uuid) return;
    if (!Array.isArray(wall.attributes) || wall.attributes.length === 0) return;
    if (attributesByUuid.has(wall.uuid)) return;

    attributesByUuid.set(wall.uuid, cloneDeep(wall.attributes));
  };

  (building.walls ?? []).forEach(rememberWallAttributes);

  ((building.rooms ?? []) as RoomWithWallRefs[]).forEach((room) => {
    (room.spaces ?? []).forEach((space) => {
      (space.walls ?? []).forEach(rememberWallAttributes);

      (space.boundaries ?? []).forEach((boundary) => {
        (boundary.walls ?? []).forEach(rememberWallAttributes);
        (boundary.edges ?? []).forEach((edge) => rememberWallAttributes(edge?.wall));
      });
    });
  });

  return attributesByUuid;
};

const preserveMissingWallAttributes = (target: IBuildingProviderContext, source: IBuildingProviderContext) => {
  const sourceAttributes = collectWallAttributesByUuid(source);

  const applyWallAttributes = (wall?: WallAttributeCarrier | null) => {
    if (!wall?.uuid) return;
    if (Array.isArray(wall.attributes) && wall.attributes.length > 0) return;

    const attributes = sourceAttributes.get(wall.uuid);
    if (!attributes) return;

    wall.attributes = cloneDeep(attributes);
  };

  (target.walls ?? []).forEach(applyWallAttributes);

  ((target.rooms ?? []) as RoomWithWallRefs[]).forEach((room) => {
    (room.spaces ?? []).forEach((space) => {
      (space.walls ?? []).forEach(applyWallAttributes);

      (space.boundaries ?? []).forEach((boundary) => {
        (boundary.walls ?? []).forEach(applyWallAttributes);
        (boundary.edges ?? []).forEach((edge) => applyWallAttributes(edge?.wall));
      });
    });
  });
};

interface IUseUndoRedo {
  resetHistory: () => void;
  undo: (setBuilding: Dispatch<SetStateAction<IBuildingProviderContext>>, specificIndex?: number) => void;
  redo: (setBuilding: Dispatch<SetStateAction<IBuildingProviderContext>>, specificIndex?: number) => void;
  registerBuildingChange: (refToBuilding: IBuildingProviderContext, reasonForChange: EHistoryAction) => void;
}

const useUndoRedo = (): IUseUndoRedo => {
  const { currentIndex, setCurrentIndex, history, setHistory } = useHistory();
  const { getCameraPositionSpritesSnapshot, updateCameraPositionSprites } = useAnnotations();

  // avoid stale currentIndex in timeouts
  const currentIndexRef = useRef<number | undefined>(currentIndex);
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  const bufferRef = useRef<
    Map<
      EHistoryAction,
      {
        timeout: ReturnType<typeof setTimeout>;
        latestBuilding: IBuildingProviderContext;
        reason: EHistoryAction;
      }
    >
  >(new Map());

  const clearBuffers = useCallback(() => {
    bufferRef.current.forEach((entry) => clearTimeout(entry.timeout));
    bufferRef.current.clear();
  }, []);

  const restoreCameraPositionSprites = useCallback(
    (cameraPositionSprites?: CameraPositionSpriteModel[]) => {
      if (cameraPositionSprites === undefined) return;

      updateCameraPositionSprites(() => cloneDeep(cameraPositionSprites));
    },
    [updateCameraPositionSprites],
  );

  const resetHistory = useCallback(() => {
    clearBuffers();
    setHistory([]);
    setCurrentIndex(undefined);
  }, [clearBuffers, setHistory, setCurrentIndex]);

  const undo = useCallback(
    (setBuildingFn: Dispatch<SetStateAction<IBuildingProviderContext>>, specificIndex?: number) => {
      clearBuffers(); // IMPORTANT: stop pending debounced commits from firing after undo

      const targetIndex = specificIndex ?? (currentIndex !== undefined ? currentIndex - 1 : 0);
      if (targetIndex < 0 || targetIndex >= history.length) return;

      const prev = history[targetIndex].building;

      setBuildingFn((current) => {
        const copy = cloneDeep({ ...prev, isSetByUndoRedo: true });

        preserveMissingWallAttributes(copy, current);

        return copy;
      });
      restoreCameraPositionSprites(history[targetIndex].cameraPositionSprites);
      setCurrentIndex(targetIndex);
    },
    [clearBuffers, currentIndex, history, restoreCameraPositionSprites, setCurrentIndex],
  );

  const redo = useCallback(
    (setBuildingFn: Dispatch<SetStateAction<IBuildingProviderContext>>, specificIndex?: number) => {
      clearBuffers(); // IMPORTANT: stop pending debounced commits from firing after redo

      const targetIndex = specificIndex ?? (currentIndex !== undefined ? currentIndex + 1 : 0);
      if (targetIndex < 0 || targetIndex >= history.length) return;

      const next = history[targetIndex].building;

      setBuildingFn((current) => {
        const copy = cloneDeep({ ...next, isSetByUndoRedo: true });

        preserveMissingWallAttributes(copy, current);

        return copy;
      });
      restoreCameraPositionSprites(history[targetIndex].cameraPositionSprites);
      setCurrentIndex(targetIndex);
    },
    [clearBuffers, currentIndex, history, restoreCameraPositionSprites, setCurrentIndex],
  );

  const commitHistoryEntry = useCallback(
    (building: IBuildingProviderContext, reason: EHistoryAction) => {
      const now = Date.now();
      let nextIndex: number | undefined;

      setHistory((prevHistory) => {
        const ci = currentIndexRef.current;

        // fork handling
        const base =
          ci !== undefined && ci < prevHistory.length - 1 ? prevHistory.slice(0, ci + 1) : [...prevHistory];

        const newEntry: IHistoryEntry = {
          building: cloneDeep({ ...building, isSetByUndoRedo: true }),
          cameraPositionSprites: cloneDeep(getCameraPositionSpritesSnapshot()),
          timestamp: now,
          action: base.length === 0 ? EHistoryAction.BUILDING_CREATED : reason,
          roomCompleted: true,
        };

        const last = base[base.length - 1];

        // preserve “creation” label when overwriting due to phantom updates
        const criticalActions = new Set<EHistoryAction>([
          EHistoryAction.ROOM_CREATED,
          EHistoryAction.ELEMENT_ADDED,
          EHistoryAction.PRODUCT_ADDED,
          EHistoryAction.MODEL2D_ADDED,
          EHistoryAction.WALL_PARTITION_ADDED,
        ]);

        if (last) {
          const timeDiff = newEntry.timestamp - last.timestamp;

          // If same action and same resulting building, don't create a second entry.
          if (last.action === newEntry.action && timeDiff <= DOUBLE_UPDATE_THRESHOLD) {
            if (isEqual(last.building, newEntry.building)) {
              nextIndex = undefined; // no change
              return prevHistory;
            }
          }

          if (timeDiff < RAPID_UPDATE_THRESHOLD) {
            const preservedAction = criticalActions.has(last.action) ? last.action : newEntry.action;
            base[base.length - 1] = { ...newEntry, action: preservedAction };
            nextIndex = base.length - 1;
            return base;
          }
        }
        if (last && last.action === EHistoryAction.ROOM_CREATED) {
          const dt = newEntry.timestamp - last.timestamp;
          if (newEntry.action === EHistoryAction.WALLS_CHANGED && dt < 2000) {
            return prevHistory; 
          }
        }

        if (last && last.action === EHistoryAction.ROOM_CREATED) {
          const dt = newEntry.timestamp - last.timestamp;
          if (newEntry.action === EHistoryAction.ROOM_CREATED && dt < 300) {
            return prevHistory; 
          }
        }
        base.push(newEntry);

        if (base.length > MAX_HISTORY_COUNT) {
          const removeCount = base.length - MAX_HISTORY_COUNT;
          base.splice(0, removeCount);
        }

        nextIndex = base.length - 1;
        return base;
      });

      if (nextIndex !== undefined) {
        setCurrentIndex(nextIndex);
      }
    },
    [getCameraPositionSpritesSnapshot, setHistory, setCurrentIndex],
  );

  const registerBuildingChange = useCallback(
    (building: IBuildingProviderContext, reason: EHistoryAction) => {
      if (actionsToIgnore.has(reason) || building.isSetByUndoRedo) return;

      const isDebounced = debounceHistoryActions.has(reason);

      if (!isDebounced) {
        commitHistoryEntry(building, reason);
        return;
      }

      const existing = bufferRef.current.get(reason);
      if (existing) clearTimeout(existing.timeout);

      const timeout = setTimeout(() => {
        const buffered = bufferRef.current.get(reason);
        if (!buffered) return;

        commitHistoryEntry(buffered.latestBuilding, buffered.reason);
        bufferRef.current.delete(reason);
      }, BUFFER_DELAY);

      bufferRef.current.set(reason, {
        latestBuilding: cloneDeep(building),
        timeout,
        reason,
      });
    },
    [commitHistoryEntry],
  );

  return {
    resetHistory,
    undo,
    redo,
    registerBuildingChange,
  };
};

export default useUndoRedo;
